import { Button } from '@getro/rombo';
import { ArrowLeftRight, File, RefreshCw, User } from 'lucide-react';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useHistory, Link } from 'react-router-dom';
import { Box, Flex, Image, Text } from 'rebass/styled-components';
import PropTypes from 'prop-types';

import ContactsEmptyStateSvg from '../../assets/empty-state-contacts.svg';
import { activeNetworkSelector, userSelector } from '../../redux/selectors';
import { useTierGating } from '../../hooks/useTierGating';
import UpgradeModal from '../../components/organisms/upgradeModal';

export const ContactsEmptyState = ({ isList }) => {
  const activeNetwork = useSelector(activeNetworkSelector);
  const { linkedinSync } = useSelector(userSelector);
  const history = useHistory();
  const { hasCapability } = useTierGating();
  const canImport = hasCapability('contacts_import');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  return (
    <Box p="4" maxWidth="600px" mt="5" mx="auto" sx={{ borderRadius: 'defaul' }}>
      <Flex sx={{ flexDirection: 'column', gap: '16px' }} textAlign="center">
        <Image
          src={ContactsEmptyStateSvg}
          alt="Getro Network"
          width={['200px', '400px']}
          height={['100px', '200px']}
          mx="auto"
        />
        <Text fontSize="22px" fontWeight="semibold" color="text.dark" px={[0, '64px']}>
          {isList ? 'No contacts in your network yet' : 'Aggregate your team’s contacts'}
        </Text>
        <Text color="text.dark" fontWeight="500">
          Once you add contacts to your network, they’ll appear here so you can build your list.
        </Text>
      </Flex>
      {!isList && (
        <Flex alignItems="center" flexDirection="column">
          {!linkedinSync?.syncStatus && (
            <>
              <Text mt="32px" fontSize="15px">
                Start syncing your LinkedIn connections:
              </Text>
              <Button
                {...(canImport
                  ? { as: NavLink, to: `/networks/${activeNetwork.slug}/linkedin/sync` }
                  : { onClick: () => setShowUpgradeModal(true) })}
                mt="16px"
                width="280px"
                justifyContent="center"
                icon={RefreshCw}
              >
                Connect LinkedIn
              </Button>
            </>
          )}

          {linkedinSync?.syncStatus && !activeNetwork.shareContacts && (
            <>
              <Text mt="32px" fontSize="15px">
                Your contacts are already synced in Getro. Share them with {activeNetwork.name} to make them visible
                here.
              </Text>
              <Button
                as={NavLink}
                mt="16px"
                width="280px"
                justifyContent="center"
                icon={ArrowLeftRight}
                to={`/networks/${activeNetwork.slug}/settings/my-networks`}
              >
                Share my contacts
              </Button>
            </>
          )}
          <Text mt="32px" fontSize="15px">
            Or start manually adding contacts:
          </Text>
          <Button
            mt="16px"
            size="small"
            variant="secondary"
            width="280px"
            justifyContent="center"
            onClick={() => {
              if (canImport) {
                history.push(`/networks/${activeNetwork.slug}/import-contacts/csv`);
              } else {
                setShowUpgradeModal(true);
              }
            }}
          >
            <Flex alignItems="center">
              <Box as={File} mr="2" width="16px" height="16px" aria-hidden="true" />
              Import CSV
            </Flex>
          </Button>
          <Button
            mt="16px"
            size="small"
            variant="secondary"
            width="280px"
            justifyContent="center"
            onClick={() => history.push(`/networks/${activeNetwork.slug}/contact/add`)}
          >
            <Flex alignItems="center">
              <Box as={User} mr="2" width="16px" height="16px" aria-hidden="true" />
              Add a single contact
            </Flex>
          </Button>
          <UpgradeModal
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            featureName="contacts_import"
          />
        </Flex>
      )}

      <Flex justifyContent="center" mt="32px">
        {isList && (
          <Button as={Link} to={`/networks/${activeNetwork.slug}/contacts`} size="medium">
            Start building your network
          </Button>
        )}
      </Flex>
    </Box>
  );
};

ContactsEmptyState.propTypes = {
  isList: PropTypes.bool,
};

ContactsEmptyState.defaultProps = {
  isList: false,
};
